/* eslint-disable @typescript-eslint/no-unused-vars */
import { ApiResponse } from 'src/app/core/models/api-response';
import { Injectable } from '@angular/core';
import { Action, StateToken, State, StateContext, Selector } from '@ngxs/store';
import { patch, updateItem, insertItem, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminWorkAndRestRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IWorkScheduleTemplate } from '../../models/work-schedule-template.model';
import { WorkScheduleTemplateApiService } from '../../services/work-schedule-template-api.service';
import {
  LoadWorkScheduleTemplates,
  LoadWorkScheduleTemplateById,
  CreateWorkScheduleTemplate,
  UpdateWorkScheduleTemplate,
  DeleteWorkScheduleTemplate,
  LoadAllWorkScheduleTemplates,
  ClearCurrentWorkScheduleTemplate,
} from './work-schedule-template.actions';

export interface WorkScheduleTemplateStateModel {
  workScheduleTemplatesLoaded: boolean;
  allWorkScheduleTemplates: IWorkScheduleTemplate[];
  workScheduleTemplates: IWorkScheduleTemplate[];
  currentWorkScheduleTemplate: IWorkScheduleTemplate | undefined;
  workScheduleTemplatesTotalPages: number;
}

const WORK_SCHEDULE_TEMPLATE_STATE_TOKEN = new StateToken<WorkScheduleTemplateStateModel>(
  'workScheduleTemplate'
);

@State<WorkScheduleTemplateStateModel>({
  name: WORK_SCHEDULE_TEMPLATE_STATE_TOKEN,
  defaults: {
    workScheduleTemplatesLoaded: false,
    workScheduleTemplates: [],
    allWorkScheduleTemplates: [],
    workScheduleTemplatesTotalPages: 0,
    currentWorkScheduleTemplate: undefined,
  },
})
@Injectable()
export class WorkScheduleTemplateState {
  constructor(private service: WorkScheduleTemplateApiService) {}

  @Action(LoadWorkScheduleTemplates)
  loadWorkScheduleTemplates(
    { getState, setState }: StateContext<WorkScheduleTemplateStateModel>,
    { filter }: LoadWorkScheduleTemplates
  ) {
    return this.service.loadWorkScheduleTemplates(filter).pipe(
      tap((response: ApiResponse<IWorkScheduleTemplate[]>) => {
        const state = getState();
        setState({
          ...state,
          workScheduleTemplatesLoaded: true,
          workScheduleTemplates: response.data,
          workScheduleTemplatesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllWorkScheduleTemplates)
  loadAllWorkScheduleTemplates({
    getState,
    setState,
  }: StateContext<WorkScheduleTemplateStateModel>) {
    return this.service.loadAllWorkScheduleTemplates().pipe(
      tap((response: IWorkScheduleTemplate[]) => {
        const state = getState();
        setState({
          ...state,
          allWorkScheduleTemplates: response,
        });
      })
    );
  }

  @Action(LoadWorkScheduleTemplateById, { cancelUncompleted: true })
  loadWorkScheduleTemplateById(
    { setState }: StateContext<WorkScheduleTemplateStateModel>,
    { id }: LoadWorkScheduleTemplateById
  ) {
    return this.service.loadWorkScheduleTemplateById(id).pipe(
      tap((workScheduleTemplate: IWorkScheduleTemplate) => {
        setState(
          patch({
            currentWorkScheduleTemplate: workScheduleTemplate,
          })
        );
      })
    );
  }

  @Action(CreateWorkScheduleTemplate)
  @OnSuccess({
    url: AdminWorkAndRestRoutes.WorkScheduleTemplates,
    message: 'workScheduleTemplateCreated',
  })
  createWorkScheduleTemplate(
    { setState }: StateContext<WorkScheduleTemplateStateModel>,
    { workScheduleTemplate }: CreateWorkScheduleTemplate
  ) {
    return this.service.createWorkScheduleTemplate(workScheduleTemplate).pipe(
      tap((response: IWorkScheduleTemplate) => {
        setState(
          patch({
            workScheduleTemplates: insertItem<IWorkScheduleTemplate>(response),
          })
        );
      })
    );
  }

  @Action(UpdateWorkScheduleTemplate)
  @OnSuccess({
    url: AdminWorkAndRestRoutes.WorkScheduleTemplates,
    message: 'workScheduleTemplateUpdated',
  })
  updateWorkScheduleTemplate(
    { setState }: StateContext<WorkScheduleTemplateStateModel>,
    { workScheduleTemplate }: UpdateWorkScheduleTemplate
  ) {
    return this.service.updateWorkScheduleTemplate(workScheduleTemplate).pipe(
      tap(() => {
        setState(
          patch({
            workScheduleTemplates: updateItem<IWorkScheduleTemplate>(
              (toUpdate) => toUpdate?.id === workScheduleTemplate.id,
              workScheduleTemplate
            ),
          })
        );
      })
    );
  }

  @Action(DeleteWorkScheduleTemplate, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminWorkAndRestRoutes.WorkScheduleTemplates,
    message: 'workScheduleTemplateDeleted',
  })
  deleteWorkScheduleTemplate(
    ctx: StateContext<WorkScheduleTemplateStateModel>,
    { id }: DeleteWorkScheduleTemplate
  ) {
    return this.service.deleteWorkScheduleTemplate(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            workScheduleTemplates: removeItem<IWorkScheduleTemplate>(
              (jtToDelete: IWorkScheduleTemplate | undefined) => jtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentWorkScheduleTemplate)
  clearCurrentWorkScheduleTemplate(ctx: StateContext<WorkScheduleTemplateStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentWorkScheduleTemplate: undefined,
    });
  }

  @Selector()
  static workScheduleTemplatesTotalPages(state: WorkScheduleTemplateStateModel) {
    return state.workScheduleTemplatesTotalPages;
  }

  @Selector()
  static workScheduleTemplates(state: WorkScheduleTemplateStateModel) {
    return state.workScheduleTemplates;
  }

  @Selector()
  static allWorkScheduleTemplates(state: WorkScheduleTemplateStateModel): IWorkScheduleTemplate[] {
    return state.allWorkScheduleTemplates;
  }

  @Selector()
  static getCurrentWorkScheduleTemplate(state: WorkScheduleTemplateStateModel) {
    return state.currentWorkScheduleTemplate;
  }
}
